import React, { useCallback, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Rating } from 'primereact/rating';
import { SelectButton } from 'primereact/selectbutton';
import { ToggleButton } from 'primereact/togglebutton';
import { InputTextarea } from 'primereact/inputtextarea';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { getByDate, create, deleteCheckIn } from '../../services/checkInService';
import { CHECKIN_QUESTIONS, getCurrentTimeSlot } from './checkInQuestions';

function AnswerInput({ question, value, onChange }) {
  switch (question.answerType) {
    case 'scale':
      return (
        <Rating
          value={value || 0}
          onChange={(e) => onChange(e.value)}
          stars={question.max || 5}
          cancel={false}
        />
      );
    case 'select':
      return (
        <SelectButton
          value={value}
          onChange={(e) => onChange(e.value)}
          options={question.options}
        />
      );
    case 'boolean':
      return (
        <ToggleButton
          checked={!!value}
          onChange={(e) => onChange(e.value)}
          onLabel="Yes"
          offLabel="No"
        />
      );
    case 'text':
      return (
        <InputTextarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          autoResize
          className="w-full"
        />
      );
    default:
      return null;
  }
}

function HistoryList({ checkIns, onDelete }) {
  if (!checkIns || checkIns.length === 0) return null;

  return (
    <ul className="list-none p-0 m-0">
      {checkIns.map((entry) => (
        <li
          key={entry._id}
          className="flex align-items-center justify-content-between gap-2 py-1"
        >
          <span>
            <strong>{entry.questionText}</strong> — {String(entry.value)}
          </span>
          <Button
            icon="pi pi-times"
            rounded
            text
            severity="danger"
            className="p-button-sm"
            style={{ width: '1.75rem', height: '1.75rem' }}
            onClick={() => onDelete(entry._id)}
            aria-label="Delete entry"
          />
        </li>
      ))}
    </ul>
  );
}

const CheckInWidget = () => {
  const [todayCheckIns, setTodayCheckIns] = useState([]);
  const [pendingValue, setPendingValue] = useState(undefined);
  const [submitError, setSubmitError] = useState('');

  const timeSlot = getCurrentTimeSlot();

  const getTodayData = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const data = await getByDate(today);
      setTodayCheckIns(data.checkIns || []);
    } catch (error) {
      console.error('Error fetching today\'s check-ins:', error);
    }
  }, []);

  React.useEffect(() => {
    getTodayData();
  }, [getTodayData]);

  const answeredIds = todayCheckIns.map((c) => c.questionId);
  const pendingQuestion = (CHECKIN_QUESTIONS[timeSlot] || []).find(
    (q) => !answeredIds.includes(q.questionId)
  );

  const handleSubmit = async () => {
    if (pendingValue === undefined || pendingValue === null || pendingValue === '') {
      setSubmitError('Please answer before submitting');
      return;
    }
    setSubmitError('');

    try {
      await create({
        type: pendingQuestion.type,
        timeSlot,
        questionId: pendingQuestion.questionId,
        questionText: pendingQuestion.questionText,
        answerType: pendingQuestion.answerType,
        value: pendingValue,
      });
      setPendingValue(undefined);
      getTodayData();
    } catch (error) {
      console.error('Error creating check-in:', error);
      console.log('Error details:', error.response ? error.response.data : error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCheckIn(id);
      getTodayData();
    } catch (error) {
      console.error('Error deleting check-in:', error);
    }
  };

  const confirmDelete = (id) => {
    confirmDialog({
      message: 'Are you sure you want to delete this entry?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => handleDelete(id),
    });
  };

  return (
    <Card className="w-full">
      <ConfirmDialog />
      <div className="flex flex-column gap-3">
        <div className="flex align-items-center justify-content-between">
          <h3 className="m-0">Check In ({timeSlot})</h3>
        </div>

        {pendingQuestion ? (
          <div className="flex flex-column gap-2">
            <label>{pendingQuestion.questionText}</label>
            <AnswerInput
              question={pendingQuestion}
              value={pendingValue}
              onChange={setPendingValue}
            />
            {submitError && <small className="p-error">{submitError}</small>}
            <Button label="Submit" icon="pi pi-check" onClick={handleSubmit} className="w-fit" />
          </div>
        ) : (
          <div>You're all caught up for this check-in window 🎉</div>
        )}

        <div className="flex flex-column gap-2">
          <strong>Today's check-ins:</strong>
          {todayCheckIns.length > 0 ? (
            <HistoryList checkIns={todayCheckIns} onDelete={confirmDelete} />
          ) : (
            <div>No check-ins yet today.</div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default CheckInWidget;